import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Fingerprint, CheckCircle2, X, Bot } from "lucide-react";

const STATUS_PILL = {
  pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  rejected: "bg-[#DC2626]/15 text-[#FCA5A5] border-[#DC2626]/40",
};

function AiVerdictBlock({ v }) {
  if (!v) return null;
  const rec = (v.recommendation || "REVIEW").toUpperCase();
  const palette = rec === "PASS" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : rec === "FAIL" ? "border-[#DC2626]/40 bg-[#DC2626]/10 text-[#FCA5A5]"
                : "border-yellow-500/40 bg-yellow-500/10 text-yellow-200";
  const matchPct = Math.round(((v.match_confidence ?? 0) * 100));
  const authPct = Math.round(((v.id_authenticity_score ?? 0) * 100));
  return (
    <div className={`border rounded p-3 ${palette} text-xs flex gap-3`} data-testid="admin-kyc-ai-verdict">
      <Bot size={18} className="shrink-0 mt-0.5" />
      <div className="space-y-1">
        <div className="font-bold uppercase tracking-widest text-[11px]">AI Verdict: {rec}</div>
        <div className="opacity-90">{v.rationale}</div>
        <div className="flex flex-wrap gap-3 pt-1">
          <span>Face match: <strong>{matchPct}%</strong></span>
          <span>ID authenticity: <strong>{authPct}%</strong></span>
          <span>ID face detected: <strong>{v.id_face_detected ? "yes" : "no"}</strong></span>
          <span>Selfie face detected: <strong>{v.selfie_face_detected ? "yes" : "no"}</strong></span>
          <span>ID visible in selfie: <strong>{v.id_card_visible_in_selfie ? "yes" : "no"}</strong></span>
        </div>
        {v.anomalies?.length > 0 && (
          <div className="pt-1"><strong>Flags:</strong> {v.anomalies.join(", ")}</div>
        )}
      </div>
    </div>
  );
}

export default function AdminKyc() {
  const [filter, setFilter] = useState("pending");
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [idImg, setIdImg] = useState("");
  const [selfieImg, setSelfieImg] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/kyc?status=${filter}`);
    setList(data);
  };
  useEffect(() => { load(); }, [filter]);

  // Whenever we pick a submission, fetch its two images
  useEffect(() => {
    const fetchImgs = async () => {
      setIdImg(""); setSelfieImg("");
      if (!active) return;
      try {
        if (active.has_id_image) {
          const { data } = await api.get(`/kyc/${active.id}/image/id`);
          setIdImg(data.image || "");
        }
        if (active.has_selfie) {
          const { data } = await api.get(`/kyc/${active.id}/image/selfie`);
          setSelfieImg(data.image || "");
        }
      } catch (e) {
        // Image fetch failure is non-blocking — thumbnails just won't render.
        console.warn("KYC image fetch failed:", e);
      }
    };
    fetchImgs();
  }, [active]);

  const approve = async (k) => {
    if (!window.confirm(`Approve KYC for ${k.user_email}? Their legal name will be locked to "${k.full_legal_name}".`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${k.id}/approve`);
      toast.success("KYC approved");
      setActive(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!active || !reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${active.id}/reject`, { reason: reason.trim() });
      toast.success("KYC rejected");
      setActive(null); setReason("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-kyc-panel">
      <div className="lg:col-span-1 glass-card p-3 flex flex-col h-[70vh]">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FBBF24] font-bold mb-3">
          <Fingerprint size={14} /> KYC Submissions
        </div>
        <div className="flex gap-1 mb-3">
          {["pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => { setFilter(s); setActive(null); }}
              className={`flex-1 px-2 py-1 rounded text-[10px] uppercase tracking-widest font-bold ${filter === s ? "bg-[#1E3A8A] text-white" : "bg-white/5 text-white/55 hover:bg-white/10"}`}
              data-testid={`kyc-filter-${s}`}
            >{s}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {list.length === 0 && <div className="text-white/40 text-xs text-center py-8">No submissions.</div>}
          {list.map((k) => {
            const rec = k.ai_verdict?.recommendation?.toUpperCase();
            const tagCls = rec === "PASS" ? "text-emerald-300" : rec === "FAIL" ? "text-[#FCA5A5]" : "text-yellow-300";
            return (
              <button
                key={k.id}
                onClick={() => { setActive(k); setReason(""); }}
                className={`w-full text-left p-3 rounded-lg border ${active?.id === k.id ? "bg-[#1E3A8A] border-[#FBBF24]" : "bg-white/[0.03] border-white/5 hover:bg-white/[0.06]"}`}
                data-testid={`kyc-row-${k.id}`}
              >
                <div className="text-sm font-semibold truncate">{k.full_legal_name}</div>
                <div className="text-[11px] text-white/55 truncate">{k.user_email}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-white/40 uppercase tracking-widest">{k.id_type.replace("_"," ")}</span>
                  {rec && <span className={`text-[10px] font-bold ${tagCls} uppercase tracking-widest`}>AI: {rec}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-2 glass-card flex flex-col h-[70vh] overflow-hidden">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-white/45 gap-2" data-testid="admin-kyc-empty">
            <Fingerprint size={36} className="text-white/30" />
            <div className="text-sm">Select a submission to review.</div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="admin-kyc-detail">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-display text-2xl font-bold">{active.full_legal_name}</div>
                <div className="text-sm text-white/55">{active.user_email}</div>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_PILL[active.status]}`}>{active.status}</span>
            </div>

            <AiVerdictBlock v={active.ai_verdict} />

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-black/30 rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/45">ID Type</div>
                <div className="mt-1">{active.id_type.replace("_"," ")}</div>
              </div>
              <div className="bg-black/30 rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/45">ID Number</div>
                <div className="mt-1 font-mono-num">{active.id_number}</div>
              </div>
              <div className="bg-black/30 rounded p-3 col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-white/45">Submitted</div>
                <div className="mt-1 text-xs">{new Date(active.submitted_at).toLocaleString()}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/45 mb-2">ID Photo</div>
                {idImg ? (
                  <img src={idImg} alt="ID" className="rounded-lg border border-white/10 max-h-80 w-full object-contain bg-black/40" data-testid="admin-kyc-id-img" />
                ) : (
                  <div className="text-xs text-white/40 italic">{active.has_id_image ? "Loading…" : "Not uploaded."}</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/45 mb-2">Selfie holding ID</div>
                {selfieImg ? (
                  <img src={selfieImg} alt="Selfie" className="rounded-lg border border-white/10 max-h-80 w-full object-contain bg-black/40" data-testid="admin-kyc-selfie-img" />
                ) : (
                  <div className="text-xs text-white/40 italic">{active.has_selfie ? "Loading…" : "Not uploaded."}</div>
                )}
              </div>
            </div>

            {active.status === "rejected" && active.reject_reason && (
              <div className="bg-[#DC2626]/10 border border-[#DC2626]/40 rounded p-3 text-xs text-[#FCA5A5]">
                <span className="uppercase tracking-widest font-bold">Reject reason:</span> {active.reject_reason}
              </div>
            )}

            {active.status === "pending" && (
              <div className="border-t border-white/5 pt-5 space-y-3">
                <button onClick={() => approve(active)} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-500 py-3 rounded-md font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="kyc-approve">
                  <CheckCircle2 size={16} /> Approve & lock legal name
                </button>
                <div>
                  <label className="zfd-label">Reason if rejecting</label>
                  <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="zfd-input" placeholder="e.g. ID photo is blurry, name doesn't match…" data-testid="kyc-reject-reason" />
                </div>
                <button onClick={reject} disabled={busy || !reason.trim()} className="w-full bg-[#DC2626] hover:bg-red-700 py-2.5 rounded-md font-bold flex items-center justify-center gap-2 disabled:opacity-40" data-testid="kyc-reject">
                  <X size={16} /> Reject
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
